import {
  isAverageAlert,
  isBaselineAlert,
} from 'src/redux/data/alerts/modules/typeGuards';
import {
  ALERT_METRIC_ENUM,
  ALERT_METRIC_PREPOSITION_ENUM,
} from 'src/redux/data/constants';
import {
  ThresholdPreposition,
  AlertType,
  Alert,
  AlertLog,
} from 'src/types/alerts';
import { defineMessages, IntlShape } from 'react-intl';
import { getLongOutOfBedTriggerValue } from 'src/utils/alertHelpers';
import { TenantBaselineThresholdsState } from 'src/components/AlertSettingsComponents/BaselineAlertSettingsForm';

export const alertIcons = {
  RR: 'Lungs',
  HR: 'Heart',
  HR_BASELINE: 'Heart',
  RR_BASELINE: 'Lungs',
  BED_EXIT: 'BedEmpty',
  LONG_OUT_OF_BED: 'BedEmpty',
  POSITION_CHANGE: 'Bed',
  BED_EXIT_FREQUENCY: 'BedEmpty',
  BED_TIME_BASELINE: 'patient-in-bed-icon',
  DEVICE_DISCONNECTED: 'wifi-disconnected-icon-v2',
  ALL_DEVICES_DISCONNECTED: 'wifi-disconnected-icon-v2',
};

export const getArrowIcons = (
  thresholdMetric: AlertType,
  thresholdPreposition?: ThresholdPreposition | null,
) => {
  if (isAverageAlert(thresholdMetric)) {
    switch (thresholdPreposition) {
      case ALERT_METRIC_PREPOSITION_ENUM.ABOVE:
        return 'ArrowUp';
      case ALERT_METRIC_PREPOSITION_ENUM.BELOW:
        return 'ArrowDown';
    }
  }

  if (isBaselineAlert(thresholdMetric)) {
    switch (thresholdPreposition) {
      case ALERT_METRIC_PREPOSITION_ENUM.ABOVE:
        return 'ArrowUpwards';
      case ALERT_METRIC_PREPOSITION_ENUM.BELOW:
        return 'ArrowDownwards';
    }
  }

  return 'ArrowRight';
};

export const getMetricHeaderProps = (
  intl: IntlShape,
  thresholdMetric: AlertType,
  alert: Alert,
) => {
  const { thresholdPreposition } = alert;
  const commonProps = {
    iconProps: {
      iconName: alertIcons[thresholdMetric],
    },
  };
  switch (thresholdMetric) {
    case ALERT_METRIC_ENUM.BED_EXIT:
      return {
        ...commonProps,
        title: intl.formatMessage(alertTitle[thresholdMetric]),
      };
    case ALERT_METRIC_ENUM.LONG_OUT_OF_BED:
      return {
        ...commonProps,
        title: intl.formatMessage(alertTitle[thresholdMetric]),
      };
    case ALERT_METRIC_ENUM.HR:
      return {
        ...commonProps,
        title:
          thresholdPreposition === ALERT_METRIC_PREPOSITION_ENUM.ABOVE
            ? intl.formatMessage(alertTitle.HIGH_HR)
            : intl.formatMessage(alertTitle.LOW_HR),
      };
    case ALERT_METRIC_ENUM.RR:
      return {
        ...commonProps,
        title:
          thresholdPreposition === ALERT_METRIC_PREPOSITION_ENUM.ABOVE
            ? intl.formatMessage(alertTitle.HIGH_RR)
            : intl.formatMessage(alertTitle.LOW_RR),
      };
    case ALERT_METRIC_ENUM.HR_BASELINE:
      return {
        ...commonProps,
        title:
          thresholdPreposition === ALERT_METRIC_PREPOSITION_ENUM.ABOVE
            ? intl.formatMessage(alertTitle.HIGH_HR_TRENDING)
            : intl.formatMessage(alertTitle.LOW_HR_TRENDING),
      };
    case ALERT_METRIC_ENUM.RR_BASELINE:
      return {
        ...commonProps,
        title:
          thresholdPreposition === ALERT_METRIC_PREPOSITION_ENUM.ABOVE
            ? intl.formatMessage(alertTitle.HIGH_RR_TRENDING)
            : intl.formatMessage(alertTitle.LOW_RR_TRENDING),
      };
    case ALERT_METRIC_ENUM.POSITION_CHANGE:
      return {
        ...commonProps,
        title: intl.formatMessage(alertTitle[thresholdMetric]),
      };
    case ALERT_METRIC_ENUM.BED_EXIT_FREQUENCY:
      return {
        ...commonProps,
        title: intl.formatMessage(alertTitle[thresholdMetric]),
      };
    default:
      return {
        title: '',
        iconProps: {
          iconName: '',
        },
      };
  }
};
export const getMetricDisplayProps = (
  intl: IntlShape,
  thresholdMetric: AlertType,
  alert: Alert | AlertLog,
  baselineThresholds: TenantBaselineThresholdsState,
) => {
  const { HR_BASELINE, RR_BASELINE } = baselineThresholds;
  const { thresholdPreposition, triggerValue, deviationPercentage } = alert;
  const longOutOfBedSuffixValue = `${triggerValue && triggerValue >= 60 ? `h+` : `m`}`;
  const longOutOfBedTriggerValue = `${getLongOutOfBedTriggerValue(triggerValue)}${longOutOfBedSuffixValue}`;
  const genericValues = {
    className: 'self-start',
  };
  switch (thresholdMetric) {
    case ALERT_METRIC_ENUM.BED_EXIT:
      return {
        ...genericValues,
        value: '',
        label: intl.formatMessage(alertDescription[thresholdMetric]),
        iconProps: {
          iconName: '',
        },
      };
    case ALERT_METRIC_ENUM.BED_EXIT_FREQUENCY:
      return {
        ...genericValues,
        value: '',
        label: intl.formatMessage(alertDescription[thresholdMetric], {
          value: triggerValue,
        }),
        iconProps: {
          iconName: '',
        },
      };
    case ALERT_METRIC_ENUM.POSITION_CHANGE:
      return {
        ...genericValues,
        value: '',
        label: '',
        iconProps: {
          iconName: '',
        },
      };
    case ALERT_METRIC_ENUM.HR:
      return {
        ...genericValues,
        value: !!triggerValue && Math.round(triggerValue),
        label: intl.formatMessage(alertDescription[thresholdMetric], {
          timeValue: alert.averageInterval ? alert.averageInterval / 60 : '--',
        }),
        iconProps: {
          iconName: getArrowIcons(thresholdMetric, thresholdPreposition),
        },
      };
    case ALERT_METRIC_ENUM.RR:
      return {
        ...genericValues,
        value: !!triggerValue && Math.round(triggerValue),
        label: intl.formatMessage(alertDescription[thresholdMetric], {
          timeValue: alert.averageInterval ? alert.averageInterval / 60 : '--',
        }),
        iconProps: {
          iconName: getArrowIcons(thresholdMetric, thresholdPreposition),
        },
      };
    case ALERT_METRIC_ENUM.HR_BASELINE:
      return {
        ...genericValues,
        value: `${deviationPercentage ? Math.floor(Math.abs(deviationPercentage)) : 0}%`,
        // TODO: hardcoded values, talk with BE
        label: intl.formatMessage(alertDescription[thresholdMetric], {
          timeValue: HR_BASELINE.deviationHoursInterval,
        }),
        iconProps: {
          iconName: getArrowIcons(thresholdMetric, thresholdPreposition),
        },
      };
    case ALERT_METRIC_ENUM.RR_BASELINE:
      return {
        ...genericValues,
        value: `${deviationPercentage ? Math.floor(Math.abs(deviationPercentage)) : 0}%`,
        // TODO: hardcoded values, talk with BE
        label: intl.formatMessage(alertDescription[thresholdMetric], {
          timeValue: RR_BASELINE.deviationHoursInterval,
        }),
        iconProps: {
          iconName: getArrowIcons(thresholdMetric, thresholdPreposition),
        },
      };
    case ALERT_METRIC_ENUM.LONG_OUT_OF_BED:
      return {
        ...genericValues,
        value: '',
        label: intl.formatMessage(alertDescription[thresholdMetric], {
          longOutOfBedTriggerValue,
        }),
        iconProps: {
          iconName: '',
        },
      };
    default:
      return {
        ...genericValues,
        value: '',
        label: '',
        iconProps: {
          iconName: '',
        },
      };
  }
};

const alertDescription = defineMessages({
  HR: {
    id: 'AlertCard.description.HR',
    defaultMessage:
      'BPM for {timeValue} {timeValue, select, 1 {minute} other {minutes}}',
  },
  RR: {
    id: 'AlertCard.description.RR',
    defaultMessage:
      'BRPM for {timeValue} {timeValue, select, 1 {minute} other {minutes}}',
  },
  LONG_OUT_OF_BED: {
    id: 'AlertCard.description.LONG_OUT_OF_BED',
    defaultMessage: 'Out of bed for {longOutOfBedTriggerValue} or more',
  },
  BED_EXIT: {
    id: 'AlertCard.description.BED_EXIT',
    defaultMessage: 'Patient is out of bed',
  },
  HR_BASELINE: {
    id: 'AlertCard.description.HR_BASELINE',
    defaultMessage:
      'BPM for {timeValue} {timeValue, select, 1 {hour} other {hours}}',
  },
  RR_BASELINE: {
    id: 'AlertCard.description.RR_BASELINE',
    defaultMessage:
      'BRPM for {timeValue} {timeValue, select, 1 {hour} other {hours}}',
  },
  POSITION_CHANGE: {
    id: 'AlertCard.description.POSITION_CHANGE',
    defaultMessage: 'More than {value}h since last position change',
  },
  BED_EXIT_FREQUENCY: {
    id: 'AlertCard.description.BED_EXIT_FREQUENCY',
    defaultMessage: '{value} abnormal bed exits during defined hours',
  },
});

export const alertMessages = defineMessages({
  hoursLabel: {
    id: 'AlertCard.title.hoursLabel',
    defaultMessage: '{hoursCount} {hoursCount, select, 1 {hour} other {hours}}',
  },
});

export const alertTitle = defineMessages({
  HIGH_HR: {
    id: 'AlertCard.title.high.HIGH_HR',
    defaultMessage: 'High Heart rate',
  },
  LOW_HR: {
    id: 'AlertCard.title.high.LOW_HR',
    defaultMessage: 'Low Heart rate',
  },
  HIGH_RR: {
    id: 'AlertCard.title.high.HIGH_RR',
    defaultMessage: 'High Respiratory Rate',
  },
  LOW_HR_TRENDING: {
    id: 'AlertCard.title.high.LOW_HR_TRENDING',
    defaultMessage: 'Low Heart rate(trending)',
  },
  HIGH_HR_TRENDING: {
    id: 'AlertCard.title.high.HIGH_HR_TRENDING',
    defaultMessage: 'High Heart rate(trending)',
  },
  LOW_RR_TRENDING: {
    id: 'AlertCard.title.high.LOW_RR_TRENDING',
    defaultMessage: 'Low respiratory rate(trending)',
  },
  HIGH_RR_TRENDING: {
    id: 'AlertCard.title.high.HIGH_RR_TRENDING',
    defaultMessage: 'High respiratory rate(trending)',
  },
  LOW_RR: {
    id: 'AlertCard.title.high.RR',
    defaultMessage: 'Low Respiratory Rate',
  },
  LONG_OUT_OF_BED: {
    id: 'AlertCard.title.LONG_OUT_OF_BED',
    defaultMessage: 'Prolonged absence from bed',
  },
  BED_EXIT: {
    id: 'AlertCard.title.BED_EXIT',
    defaultMessage: 'Bed exit',
  },
  POSITION_CHANGE: {
    id: 'AlertCard.title.POSITION_CHANGE',
    defaultMessage: 'Position change required',
  },
  BED_EXIT_FREQUENCY: {
    id: 'AlertCard.title.BED_EXIT_FREQUENCY',
    defaultMessage: 'Abnormal Bed Exit Frequency',
  },
});
